"use client";

import { ArrowRight, Check } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import Image from "next/image";
import Link from "next/link";
import React, { useMemo, useState } from "react";

import { cn } from "@/core/utils/utils";
import { ALL_PROJECTS, FILTER_OPTIONS } from "../data/projects";

export interface WorkShowcaseProps {
  showFilters?: boolean;
  limit?: number;
  projectIds?: string[];
}

export function WorkShowcase({
  showFilters = true,
  limit,
  projectIds,
}: WorkShowcaseProps) {
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    let list = ALL_PROJECTS;

    // Filter by specific project IDs if specified (for contextual service/solutions pages)
    if (projectIds && projectIds.length > 0) {
      list = list.filter((p) => projectIds.includes(p.id));
    }

    // Filter by category tag if filters are active
    if (showFilters && activeFilter !== "all") {
      list = list.filter((p) => p.filterTags.includes(activeFilter));
    }

    // Apply limit if specified
    if (limit && limit > 0) {
      list = list.slice(0, limit);
    }

    return list;
  }, [activeFilter, showFilters, projectIds, limit]);

  return (
    <div className="w-full space-y-12">
      {/* ─────────────────────────────────────────────────────────────
          1. CHECKBOX FILTER BAR (Rendered only if showFilters === true)
          ───────────────────────────────────────────────────────────── */}
      {showFilters && (
        <div className="w-full flex flex-wrap items-center gap-x-8 gap-y-4 pb-8 md:pb-12">
          {FILTER_OPTIONS.map((f) => {
            const isSelected = activeFilter === f.id;

            return (
              <button
                key={f.id}
                onClick={() => setActiveFilter(f.id)}
                className="inline-flex items-center gap-2.5 group cursor-pointer py-1.5 focus:outline-none"
              >
                <span
                  className={cn(
                    "w-4 h-4 rounded-[4px] border transition-all duration-200 flex items-center justify-center shrink-0",
                    isSelected
                      ? "bg-white border-white text-black"
                      : "border-white/25 bg-white/5 group-hover:border-white/50 text-transparent"
                  )}
                >
                  <Check className="w-2.5 h-2.5 stroke-[3.5]" />
                </span>
                <span
                  className={cn(
                    "text-[11px] sm:text-xs font-mono tracking-wider transition-colors duration-200 uppercase",
                    isSelected
                      ? "text-white font-semibold"
                      : "text-zinc-400 group-hover:text-zinc-200"
                  )}
                >
                  {f.label}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          2. DESKTOP VIEW: Motion Native Layout & Expansion
          ───────────────────────────────────────────────────────────── */}
      <div className="hidden md:block border-t border-white/[0.08]">
        {filtered.map((project) => {
          const isOpen = hoveredId === project.id;

          return (
            <motion.div
              key={project.id}
              layout
              tabIndex={0}
              role="region"
              aria-label={`${project.name} Case Study`}
              aria-expanded={isOpen}
              onHoverStart={() => setHoveredId(project.id)}
              onHoverEnd={() => setHoveredId(null)}
              onClick={() => setHoveredId(isOpen ? null : project.id)}
              onFocus={() => setHoveredId(project.id)}
              onBlur={() => setHoveredId(null)}
              transition={{ layout: { duration: 0.65, ease: [0.19, 1, 0.22, 1] } }}
              className={cn(
                "w-full border-b border-white/[0.08] p-8 lg:p-12 transition-all duration-500 ease-out relative cursor-pointer select-none outline-none focus-visible:ring-1 focus-visible:ring-white/30",
                isOpen
                  ? cn(project.themeColor, "rounded-3xl shadow-2xl border-transparent text-white")
                  : "bg-transparent text-white hover:bg-white/[0.02]"
              )}
            >
              {/* 3-Column Header Grid */}
              <div className="grid grid-cols-12 gap-8 items-start">
                
                {/* Column 1: Title & View Case Study Link */}
                <div className="col-span-4 space-y-2">
                  <h3 className="text-2xl lg:text-3xl font-bold tracking-tight text-white">
                    {project.name}
                  </h3>

                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.35, ease: [0.19, 1, 0.22, 1] }}
                        className="pt-2"
                      >
                        <Link
                          href={`/work/${project.slug}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-2 text-sm lg:text-base font-medium text-white underline underline-offset-8 hover:opacity-80 transition-opacity group"
                        >
                          <span>View Case Study</span>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Column 2: 2-3 Line Summary */}
                <div className="col-span-5">
                  <p
                    className={cn(
                      "text-sm lg:text-base leading-relaxed transition-colors duration-400",
                      isOpen ? "text-white/95 font-normal" : "text-slate-400 font-light"
                    )}
                  >
                    {project.summary}
                  </p>
                </div>

                {/* Column 3: Service Tags Stack & Location */}
                <div className="col-span-3 flex flex-col justify-between space-y-4 text-right">
                  <div className="space-y-1">
                    {project.services.map((s, idx) => (
                      <div
                        key={idx}
                        className={cn(
                          "text-[11px] lg:text-xs font-mono tracking-wider uppercase transition-colors duration-400",
                          isOpen ? "text-white/85" : "text-slate-400"
                        )}
                      >
                        {s}
                      </div>
                    ))}
                  </div>
                  <div
                    className={cn(
                      "text-xs font-mono font-bold tracking-widest uppercase transition-colors duration-400",
                      isOpen ? "text-white" : "text-slate-400"
                    )}
                  >
                    {project.location}
                  </div>
                </div>

              </div>

              {/* Expandable Images */}
              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{
                      height: { duration: 0.65, ease: [0.19, 1, 0.22, 1] },
                      opacity: { duration: 0.45, ease: "easeOut" }
                    }}
                    className="overflow-hidden pt-10"
                  >
                    <div className="grid grid-cols-12 gap-4 lg:gap-6 items-stretch">
                      
                      <motion.div
                        initial={{ opacity: 0, y: 16, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1], delay: 0.05 }}
                        className="col-span-6 rounded-2xl overflow-hidden shadow-2xl bg-black/20 border border-white/10"
                      >
                        <div className="relative w-full aspect-[16/10]">
                          <Image
                            src={project.screenshots[0].src}
                            alt={project.screenshots[0].alt}
                            fill
                            className="object-cover"
                            sizes="50vw"
                          />
                        </div>
                      </motion.div>

                      <motion.div
                        initial={{ opacity: 0, y: 16, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1], delay: 0.1 }}
                        className="col-span-3 rounded-2xl overflow-hidden shadow-2xl bg-black/20 border border-white/10"
                      >
                        <div className="relative w-full aspect-[4/3] h-full">
                          <Image
                            src={project.screenshots[1].src}
                            alt={project.screenshots[1].alt}
                            fill
                            className="object-cover"
                            sizes="25vw"
                          />
                        </div>
                      </motion.div>

                      <motion.div
                        initial={{ opacity: 0, y: 16, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1], delay: 0.15 }}
                        className="col-span-3 rounded-2xl overflow-hidden shadow-2xl bg-black/20 border border-white/10"
                      >
                        <div className="relative w-full aspect-[4/3] h-full">
                          <Image
                            src={project.screenshots[2].src}
                            alt={project.screenshots[2].alt}
                            fill
                            className="object-cover"
                            sizes="25vw"
                          />
                        </div>
                      </motion.div>

                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

            </motion.div>
          );
        })}
      </div>

      {/* ─────────────────────────────────────────────────────────────
          3. MOBILE VIEW: Single-Screenshot Clean Cards
          ───────────────────────────────────────────────────────────── */}
      <div className="block md:hidden space-y-6">
        {filtered.map((project) => (
          <div
            key={project.id}
            className={cn(
              "w-full rounded-[28px] p-6 text-white shadow-xl overflow-hidden",
              project.themeColor
            )}
          >
            <h3 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4">
              {project.name}
            </h3>

            <div className="space-y-1 mb-6">
              {project.services.map((s, idx) => (
                <div
                  key={idx}
                  className="text-[11px] font-mono tracking-wider uppercase opacity-85"
                >
                  {s}
                </div>
              ))}
            </div>

            <div className="rounded-2xl overflow-hidden shadow-2xl bg-black/30 border border-white/10 mb-6">
              <div className="relative w-full aspect-[16/10]">
                <Image
                  src={project.screenshots[0].src}
                  alt={project.screenshots[0].alt}
                  fill
                  className="object-cover"
                  sizes="100vw"
                />
              </div>
            </div>

            <div className="pt-2 border-t border-white/10">
              <Link
                href={`/work/${project.slug}`}
                className="flex items-center justify-between text-base font-medium text-white underline underline-offset-8 group"
              >
                <span>View Case Study</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        ))}
      </div>

    </div>
  );
}
